import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stepper, Step, StepLabel, CircularProgress, Alert } from '@mui/material';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';

const StepperModal = ({
  open,
  onClose,
  title,
  steps,
  activeStep,
  onNext,
  onBack,
  onSubmit,
  canProceed,
  loading,
  error,
  submitButtonText = 'Submit',
  nextButtonText = 'Next',
  backButtonText = 'Back',
  cancelButtonText = 'Cancel',
  width = 560,
  renderStepContent,
  ariaLabelledby,
  ariaDescribedby,
}) => {
  const { colors } = useThemeStyles();

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleNext = () => {
    if (canProceed && canProceed()) {
      onNext();
    }
  };

  const handleBack = () => {
    if (!loading) {
      onBack();
    }
  };

  const handleSubmit = () => {
    if (canProceed && canProceed()) {
      onSubmit();
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title={title}
      width={width}
      ariaLabelledby={ariaLabelledby}
      ariaDescribedby={ariaDescribedby}
      showCloseButton
      contentSx={{ gap: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Fixed Header: Stepper and Error */}
      <Box sx={{ flexShrink: 0 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            '& .MuiStepLabel-label': {
              color: colors?.text?.secondary || 'text.secondary',
              '&.Mui-active,&.Mui-completed': {
                color: colors?.text?.primary || 'text.primary',
              },
            },
            '& .MuiStepIcon-root': {
              color: colors?.border?.subtle || 'rgba(148, 163, 184, 0.8)',
              '&.Mui-active,&.Mui-completed': {
                color: colors?.primary?.main || 'primary.main',
              },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors?.border?.subtle || 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors?.border?.primary || 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <Box sx={{ minHeight: 280 }}>{renderStepContent()}</Box>
      </Box>

      {/* Fixed Footer: Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${colors?.border?.subtle || 'divider'}`,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
          variant="outlined"
          sx={{
            color: colors?.text?.secondary || 'text.secondary',
            borderColor: colors?.border?.subtle || 'divider',
            '&:hover': {
              color: colors?.text?.primary || 'text.primary',
              borderColor: colors?.border?.primary || 'primary.main',
            },
          }}
        >
          {backButtonText}
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              color: colors?.text?.secondary || 'text.secondary',
              borderColor: colors?.border?.subtle || 'divider',
              '&:hover': {
                color: colors?.text?.primary || 'text.primary',
                borderColor: colors?.border?.primary || 'primary.main',
              },
            }}
          >
            {cancelButtonText}
          </Button>

          {!isLastStep ? (
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={!canProceed || !canProceed() || loading}
              sx={{
                minWidth: 96,
                '&.Mui-disabled': {
                  color: colors?.text?.secondary || 'text.secondary',
                  backgroundColor: colors?.background?.secondary || 'rgba(30, 64, 175, 0.4)',
                  opacity: 1,
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : nextButtonText}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!canProceed || !canProceed() || loading}
              sx={{
                minWidth: 96,
                '&.Mui-disabled': {
                  color: colors?.text?.secondary || 'text.secondary',
                  backgroundColor: colors?.background?.secondary || 'rgba(30, 64, 175, 0.4)',
                  opacity: 1,
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : submitButtonText}
            </Button>
          )}
        </Box>
      </Box>
    </ReusableModal>
  );
};

StepperModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeStep: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  canProceed: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  submitButtonText: PropTypes.string,
  nextButtonText: PropTypes.string,
  backButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  renderStepContent: PropTypes.func.isRequired,
  ariaLabelledby: PropTypes.string,
  ariaDescribedby: PropTypes.string,
};

StepperModal.defaultProps = {
  loading: false,
  error: null,
  submitButtonText: 'Submit',
  nextButtonText: 'Next',
  backButtonText: 'Back',
  cancelButtonText: 'Cancel',
  width: 560,
  ariaLabelledby: undefined,
  ariaDescribedby: undefined,
};

export default StepperModal;
